import React, { useState, useEffect } from 'react';
import {
  Bell, Calendar, Coffee, DollarSign, User, Clock,
  Check, X, Settings, Filter, Search, AlertTriangle, RefreshCw
} from 'lucide-react';
import { MeetingStorage } from '../utils/meetingStorage';
import { logger } from '../utils/logger';

const NotificationsView = ({ userType, currentUser }) => {
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load notifications from MeetingStorage
  useEffect(() => {
    loadNotifications();
  }, [currentUser, userType]);

  const loadNotifications = () => {
    try {
      if (!currentUser) return;

      // Get notifications from MeetingStorage
      const meetingNotifications = MeetingStorage.getNotificationsForUser(currentUser.id, userType);

      // Transform meeting notifications to UI format
      const formattedNotifications = meetingNotifications.map(notif => ({
        id: notif.id,
        type: notif.type,
        title: getNotificationTitle(notif.type),
        message: notif.message,
        time: getTimeAgo(notif.createdAt),
        read: notif.read,
        icon: getNotificationIcon(notif.type),
        action: getNotificationAction(notif.type),
        meetingId: notif.meetingId,
        data: notif.data,
        urgent: notif.urgent
      }));

      // Add some static notifications for demo purposes
      const staticNotifications = [
        {
          id: 'static-1',
          type: 'compliance_reminder',
          title: 'Compliance Report Due',
          message: 'Your monthly compliance report is due in 3 days',
          time: '5 hours ago',
          read: false,
          icon: <DollarSign className="w-5 h-5 text-yellow-600" />,
          action: 'Generate Report'
        }
      ];

      // Combine and sort notifications by creation date
      const allNotifications = [...formattedNotifications, ...staticNotifications]
        .sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt) : new Date(Date.now() - 5 * 60 * 60 * 1000);
          const timeB = b.createdAt ? new Date(b.createdAt) : new Date(Date.now() - 5 * 60 * 60 * 1000);
          return timeB - timeA;
        });

      setNotifications(allNotifications);
    } catch (error) {
      logger.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationTitle = (type) => {
    switch (type) {
      case 'meeting_cancelled_by_customer': return 'Meeting Cancelled';
      case 'meeting_cancelled_by_sales': return 'Meeting Cancelled';
      case 'meeting_approved': return 'Meeting Confirmed';
      case 'meeting_declined': return 'Meeting Declined';
      case 'meeting_request': return 'New Meeting Request';
      case 'reschedule_suggestion': return 'Reschedule Suggested';
      case 'reschedule_request': return 'Reschedule Request';
      default: return 'Notification';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'meeting_cancelled_by_customer':
      case 'meeting_cancelled_by_sales':
        return <X className="w-5 h-5 text-red-600" />;
      case 'meeting_approved':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'meeting_declined':
        return <X className="w-5 h-5 text-orange-600" />;
      case 'meeting_request':
        return <User className="w-5 h-5 text-blue-600" />;
      case 'reschedule_suggestion':
      case 'reschedule_request':
        return <RefreshCw className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getNotificationAction = (type) => {
    switch (type) {
      case 'meeting_cancelled_by_customer':
      case 'meeting_cancelled_by_sales':
      case 'meeting_approved':
      case 'meeting_declined':
        return 'View Details';
      case 'meeting_request':
        return 'Respond';
      case 'reschedule_suggestion':
      case 'reschedule_request':
        return 'Review';
      default:
        return null;
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';

    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;

    return time.toLocaleDateString();
  };

  const markAsRead = (id) => {
    // Update local state
    setNotifications(notifications.map(notif =>
      notif.id === id ? { ...notif, read: true } : notif
    ));

    // Update MeetingStorage if it's a meeting notification
    const notification = notifications.find(n => n.id === id);
    if (notification && !notification.id.toString().startsWith('static-')) {
      try {
        MeetingStorage.markNotificationAsRead(id);
      } catch (error) {
        logger.error('Error marking notification as read:', error);
      }
    }
  };

  const markAllAsRead = () => {
    // Update local state
    setNotifications(notifications.map(notif => ({ ...notif, read: true })));

    // Update MeetingStorage for meeting notifications
    notifications.forEach(notif => {
      if (!notif.id.toString().startsWith('static-') && !notif.read) {
        try {
          MeetingStorage.markNotificationAsRead(notif.id);
        } catch (error) {
          logger.error('Error marking notification as read:', error);
        }
      }
    });
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(notif => notif.id !== id));
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notif.read;
    return notif.type === filter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading notifications...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Notifications</h2>
            <p className="text-gray-600 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          <div className="flex space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
              >
                <Check className="w-4 h-4 mr-1" />
                Mark all as read
              </button>
            )}
            <button className="text-gray-600 hover:text-gray-800">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {[
              { id: 'all', label: 'All', count: notifications.length },
              { id: 'unread', label: 'Unread', count: unreadCount },
              { id: 'meeting_confirmation', label: 'Meetings', count: notifications.filter(n => n.type === 'meeting_confirmation' || n.type === 'meeting_request').length },
              { id: 'compliance_reminder', label: 'Compliance', count: notifications.filter(n => n.type === 'compliance_reminder').length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  filter === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-lg shadow-md p-4 transition-all hover:shadow-lg ${
                !notification.read
                  ? notification.urgent
                    ? 'border-l-4 border-red-500 bg-red-50'
                    : 'border-l-4 border-blue-500'
                  : ''
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {notification.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                        {notification.title}
                      </h4>
                      <p className="text-gray-600 text-sm mt-1">{notification.message}</p>
                      <p className="text-gray-400 text-xs mt-2 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {notification.time}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {notification.action && (
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          {notification.action}
                        </button>
                      )}
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'No notifications yet' 
                : `No ${filter === 'unread' ? 'unread' : filter.replace('_', ' ')} notifications`
              }
            </p>
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          {[
            { label: 'Meeting confirmations', key: 'meeting_confirmations', enabled: true },
            { label: 'New meeting requests', key: 'meeting_requests', enabled: true },
            { label: 'Order status updates', key: 'order_updates', enabled: true },
            { label: 'Compliance reminders', key: 'compliance_reminders', enabled: userType === 'sales' },
            { label: 'System announcements', key: 'system_announcements', enabled: false }
          ].map((pref) => (
            <div key={pref.key} className="flex items-center justify-between">
              <span className="text-gray-700">{pref.label}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked={pref.enabled}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationsView;